import { type Api, ApiError, IdbStore, RhemaClient, type SignOutCheck, type User } from "@rhema/client";
import { initCrypto } from "@rhema/crypto";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useNavigate } from "react-router";
import { OPERATOR_KEY_FINGERPRINT } from "../backup/operatorKey";
import { currentPlatform, type Platform } from "../platform";
import { createWebPlatform } from "../platform/web";

/** Everything the session needs from the outside world, so tests can swap in fakes. */
export type SessionDeps = {
  api: Api;
  initCrypto: () => Promise<void>;
  /** `owner` names the device's store and keys. It is an older id after accounts are combined. */
  createClient: (api: Api, me: User, owner?: string) => RhemaClient;
  destroyStore: (userId: string) => Promise<void>;
  /**
   * The id this device keeps the person's keys and history under. After two accounts are combined,
   * a device that was signed in to the one combined away keeps its store under that account's id.
   */
  storeOwner?: (me: User) => Promise<string>;
  redirect: (url: string) => void;
  /** The browser, desktop or mobile shell. Tests leave it out and get the web platform. */
  platform?: Platform;
};

export const storeName = (userId: string) => `rhema-${userId}`;

export function defaultDeps(platform: Platform = currentPlatform()): SessionDeps {
  return {
    // The web app shares the API's origin and signs in with the httpOnly session cookie. Desktop and
    // mobile send a bearer token to the server origin.
    api: platform.api,
    platform,
    initCrypto: () => initCrypto(),
    createClient: (api, me, owner = me.id) =>
      new RhemaClient({
        api,
        store: new IdbStore(storeName(owner)),
        deviceName: platform.deviceName(),
        platform: platform.device,
        ...platform.clientOptions(owner),
        oldStores: () => oldStores(platform, me, owner),
        operatorKeyFingerprint: OPERATOR_KEY_FINGERPRINT,
      }),
    destroyStore: (userId) => IdbStore.destroy(storeName(userId)),
    storeOwner: async (me) => {
      for (const id of [me.id, ...(me.merged_ids ?? [])]) {
        if ((await IdbStore.exists(storeName(id))) === true) return id;
      }
      return me.id;
    },
    redirect: (url) => window.location.assign(url),
  };
}

/**
 * Other stores of this person on this device whose pickle key is still here, such as the one kept
 * under an account merged into this one. Their message keys are imported, and the stores are left
 * as they are.
 */
async function oldStores(platform: Platform, me: User, owner: string) {
  const out: { id: string; store: IdbStore; pickleKey: Uint8Array }[] = [];
  for (const id of [me.id, ...(me.merged_ids ?? [])]) {
    if (id === owner || (await IdbStore.exists(storeName(id))) !== true) continue;
    const store = new IdbStore(storeName(id));
    const pickleKey =
      (await platform.existingPickleKey?.(id).catch(() => null)) ??
      (await store.get<Uint8Array>("pickle-key").catch(() => undefined)) ??
      null;
    if (pickleKey) out.push({ id, store, pickleKey });
  }
  return out;
}

const DepsContext = createContext<SessionDeps | null>(null);

export function SessionDepsProvider({ deps, children }: { deps: SessionDeps; children: ReactNode }) {
  return <DepsContext.Provider value={deps}>{children}</DepsContext.Provider>;
}

export type SessionStatus = "loading" | "signed-out" | "starting" | "ready" | "error";

export type Session = {
  status: SessionStatus;
  /** Starting on a device with no keys yet, which takes longer than a returning launch. */
  firstRun: boolean;
  me: User | null;
  client: RhemaClient | null;
  api: Api;
  error: string | null;
  /** Call after a password or MFA sign-in returns the user. */
  signedIn: (user: User) => void;
  /**
   * Signs out. With `keep`, this device's keys and messages stay, so signing in again to the same
   * account reads them. Call `checkSignOut` first.
   */
  signOut: (options?: { keep?: boolean }) => Promise<void>;
  /** Backs up this device's message keys and says whether signing out would lose messages. */
  checkSignOut: () => Promise<SignOutCheck>;
  setMe: (user: User) => void;
  retry: () => void;
  /** A full-page navigation, as the redirect sign-in flows need. */
  redirect: (url: string) => void;
  /** Starts again as whoever the session is signed in as now, as after accounts are combined. */
  restart: () => void;
  /** A one-time message that outlives a restart, such as that two accounts were combined. */
  notice: string | null;
  setNotice: (text: string | null) => void;
  platform: Platform;
};

const SessionContext = createContext<Session | null>(null);

type Inner = {
  status: SessionStatus;
  me: User | null;
  client: RhemaClient | null;
  error: string | null;
  /** This device has no keys yet, so starting creates them. */
  firstRun?: boolean;
};

const OFFLINE = "Rhema could not reach the server. Check your connection and try again.";

export function SessionProvider({ children }: { children: ReactNode }) {
  const injected = useContext(DepsContext);
  const deps = useMemo(() => injected ?? defaultDeps(), [injected]);
  const platform = useMemo(() => deps.platform ?? createWebPlatform(deps.api), [deps]);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const detach = useRef<(() => void) | null>(null);
  const [inner, setInner] = useState<Inner>({ status: "loading", me: null, client: null, error: null });
  const [notice, setNotice] = useState<string | null>(null);
  // One client per signed-in user, even when React runs effects twice in development.
  const startedFor = useRef<string | null>(null);
  // The id the running client's store is kept under.
  const ownerRef = useRef<string | null>(null);
  const restartRef = useRef<(() => void) | null>(null);

  const start = useCallback(
    async (me: User) => {
      if (startedFor.current === me.id) return;
      startedFor.current = me.id;
      setInner({ status: "starting", me, client: null, error: null });
      let client: RhemaClient | null = null;
      try {
        await deps.initCrypto();
        const owner = deps.storeOwner ? await deps.storeOwner(me) : me.id;
        ownerRef.current = owner;
        client = deps.createClient(deps.api, me, owner);
        if (await client.isNewDevice()) setInner((s) => ({ ...s, firstRun: true }));
        await client.start(me);
        detach.current?.();
        detach.current = platform.attach(client, { navigate: (path) => navigateRef.current(path) });
        const running = client;
        // This person's accounts were combined. As the survivor, the client has fetched everything
        // again. As the account combined away, the device is signed in as the survivor now and
        // starts again as it, keeping its keys and history.
        running.onAccountMerged((survivor) => {
          if (survivor !== me.id) return restartRef.current?.();
          const updated = running.getState().me;
          if (updated) setInner((s) => (s.client === running ? { ...s, me: updated } : s));
        });
        setInner({ status: "ready", me, client, error: null });
      } catch (err) {
        client?.stop();
        startedFor.current = null;
        if (err instanceof ApiError && err.status === 401) {
          setInner({ status: "signed-out", me: null, client: null, error: null });
        } else {
          setInner({ status: "error", me, client: null, error: OFFLINE });
        }
      }
    },
    [deps, platform],
  );

  const load = useCallback(async () => {
    try {
      const me = await deps.api.me();
      await start(me);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setInner({ status: "signed-out", me: null, client: null, error: null });
      } else {
        setInner({ status: "error", me: null, client: null, error: OFFLINE });
      }
    }
  }, [deps, start]);

  useEffect(() => {
    void load();
  }, [load]);

  restartRef.current = () => {
    detach.current?.();
    detach.current = null;
    inner.client?.stop();
    startedFor.current = null;
    void load();
  };

  // A desktop or mobile sign-in that finished from a rhema:// link.
  useEffect(() => platform.onSignedIn((user) => void start(user)), [platform, start]);

  const signOut = useCallback(
    async (options: { keep?: boolean } = {}) => {
      const keep = Boolean(options.keep);
      const owner = ownerRef.current;
      const also = owner && owner !== inner.me?.id ? [owner] : [];
      const url = await platform.signOut(inner.me?.id ?? null, { keep, also });
      detach.current?.();
      detach.current = null;
      inner.client?.stop();
      // Kept, the store and its key bring this device's messages back on the next sign-in.
      if (!keep) {
        if (inner.me) await deps.destroyStore(inner.me.id);
        for (const id of also) await deps.destroyStore(id);
      }
      startedFor.current = null;
      deps.redirect(url);
    },
    [deps, platform, inner.client, inner.me],
  );

  const checkSignOut = useCallback(
    async (): Promise<SignOutCheck> => (inner.client ? inner.client.prepareSignOut() : { safe: true }),
    [inner.client],
  );

  const value = useMemo<Session>(
    () => ({
      ...inner,
      firstRun: inner.firstRun ?? false,
      api: deps.api,
      redirect: deps.redirect,
      restart: () => restartRef.current?.(),
      notice,
      setNotice,
      platform,
      signedIn: (user) => void start(user),
      signOut,
      checkSignOut,
      setMe: (user) => setInner((s) => ({ ...s, me: user })),
      retry: () => {
        setInner((s) => ({ ...s, status: "loading", error: null }));
        void (inner.me ? start(inner.me) : load());
      },
    }),
    [inner, deps.api, deps.redirect, platform, start, load, signOut, checkSignOut, notice],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error("useSession must be used inside SessionProvider");
  return session;
}

export function useApi(): Api {
  return useSession().api;
}

/** The running client. Only call this below RequireSession, where it is always ready. */
export function useClient(): RhemaClient {
  const { client } = useSession();
  if (!client) throw new Error("useClient needs a signed-in session");
  return client;
}

export function useClientState() {
  const client = useClient();
  return useSyncExternalStore(client.subscribe, client.getState, client.getState);
}

export function useMe(): User {
  const { me } = useSession();
  if (!me) throw new Error("useMe needs a signed-in session");
  return me;
}
